import { useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Car, ChevronLeft, Zap, Flame, Leaf } from 'lucide-react'
import EmployeeSearch from './components/EmployeeSearch'
import ManualSubForm from './components/ManualSubForm'
import type { EmployeeAutoFill } from './components/EmployeeSearch'
import PayslipUpload from './components/PayslipUpload'
import PayslipConfirmModal from './components/PayslipConfirmModal'
import BenefitSummary from './components/BenefitSummary'
import EligibilityResult from './components/EligibilityResult'
import type { ParsedPayslip } from './services/payslipParser'
import { formatCurrency, InfoTooltip, inputStyles, labelStyles } from './JobAutoCalculatorHelpers'
import { computeExistingMonthly, bruttoToNettoRate, BAV_ASSUMPTION_MONTHLY, DEFAULT_MINDESTGRENZE_ANNUAL } from './jobautoCalc'
import { cn } from '../../shared'

interface SelectedSub {
  carLabel: string
  make: string | null
  model: string | null
  fuel: string | null
  gehaltsumwandlung: number
  pendingApproval: boolean
  isSwap: boolean
  previousSubscriptionId: string | null
}

interface DeductionItems {
  bavAN: number
  firmenfitness: number
  carGehaltsumwandlung: number
  carGwv: number
  fahrradGehaltsumwandlung: number
}

function FuelBadge({ fuel }: { fuel: string | null }) {
  if (!fuel) return null
  const f = fuel.toLowerCase()
  if (f.includes('electric') || f === 'bev' || f === 'elektro')
    return <span className="inline-flex items-center gap-1 text-sm px-2.5 py-1 rounded-full bg-green-100 text-green-700 font-medium"><Zap className="h-3.5 w-3.5" />Elektro</span>
  if (f.includes('hybrid') || f === 'phev' || f === 'plugin')
    return <span className="inline-flex items-center gap-1 text-sm px-2.5 py-1 rounded-full bg-blue-100 text-blue-700 font-medium"><Leaf className="h-3.5 w-3.5" />Hybrid</span>
  return <span className="inline-flex items-center gap-1 text-sm px-2.5 py-1 rounded-full bg-zinc-100 text-zinc-600 font-medium"><Flame className="h-3.5 w-3.5" />Verbrenner</span>
}

function StepIndicator({ current }: { current: 1 | 2 }) {
  return (
    <div className="flex items-center gap-2">
      {([1, 2] as const).map((n) => (
        <div key={n} className="flex items-center gap-2">
          <div className={cn(
            'w-7 h-7 rounded-full flex items-center justify-center text-sm font-semibold border-2 transition-colors',
            current === n
              ? 'bg-blue-600 border-blue-600 text-white'
              : current > n
              ? 'bg-white border-zinc-500 text-zinc-600'
              : 'bg-white border-zinc-300 text-zinc-300'
          )}>
            {n}
          </div>
          <span className={cn(
            'text-sm transition-colors',
            current === n ? 'font-semibold text-zinc-900' : current > n ? 'text-zinc-500' : 'text-zinc-300'
          )}>
            {n === 1 ? 'Subscription' : 'Gehaltscheck'}
          </span>
          {n < 2 && <div className="w-8 h-px bg-zinc-300 mx-1" />}
        </div>
      ))}
    </div>
  )
}

export default function JobAutoCalculator() {
  const [searchParams] = useSearchParams()
  const initialEmail = searchParams.get('email') ?? ''

  const [step, setStep] = useState<1 | 2>(1)
  const [mode, setMode] = useState<'search' | 'manual'>('search')
  const [selected, setSelected] = useState<SelectedSub | null>(null)
  const [bruttoMonatlich, setBruttoMonatlich] = useState(0)
  const [deductions, setDeductions] = useState<DeductionItems>({
    bavAN: 0,
    firmenfitness: 0,
    carGehaltsumwandlung: 0,
    carGwv: 0,
    fahrradGehaltsumwandlung: 0,
  })
  const [bavAssumed, setBavAssumed] = useState(false)
  const [monateImJahr, setMonateImJahr] = useState(12)
  const [mindestgrenze, setMindestgrenze] = useState(DEFAULT_MINDESTGRENZE_ANNUAL)
  const [parsedPayslip, setParsedPayslip] = useState<ParsedPayslip | null>(null)
  const [excludedCarIndices, setExcludedCarIndices] = useState<ReadonlySet<number>>(new Set())
  const [showEditModal, setShowEditModal] = useState(false)

  const handleAutoFill = (fill: EmployeeAutoFill) => {
    const parts = fill.carLabel.split(' ')
    setSelected({
      carLabel: fill.carLabel,
      make: parts[0] ?? null,
      model: parts.slice(1).join(' ') || null,
      fuel: fill.fuel,
      gehaltsumwandlung: fill.gehaltsumwandlung,
      pendingApproval: fill.pendingApproval,
      isSwap: fill.isSwap,
      previousSubscriptionId: fill.previousSubscriptionId,
    })
    // Reset payslip-derived state so a previously checked employee's data never
    // bleeds into a newly selected subscription.
    setBruttoMonatlich(0)
    setDeductions({ bavAN: 0, firmenfitness: 0, carGehaltsumwandlung: 0, carGwv: 0, fahrradGehaltsumwandlung: 0 })
    setBavAssumed(false)
    setParsedPayslip(null)
    setExcludedCarIndices(new Set())
    setStep(2)
  }

  const applyParsedPayslip = (data: ParsedPayslip) => {
    if (data.bruttogehalt != null) setBruttoMonatlich(data.bruttogehalt)
    setParsedPayslip(data)
    setExcludedCarIndices(new Set())
    // Sofia (People): assume a conservative 430 €/mo bAV when none is on the
    // payslip, flagged as an assumption in the result breakdown.
    const detectedBav = data.bavBeitragAN ?? 0
    const noBav = detectedBav <= 0
    setBavAssumed(noBav)
    setDeductions({
      bavAN: noBav ? BAV_ASSUMPTION_MONTHLY : detectedBav,
      firmenfitness: data.firmenfitnessBeitrag ?? 0,
      carGehaltsumwandlung: (data.dienstwagen ?? []).reduce((s, d) => s + (d.gehaltsumwandlung ?? 0), 0),
      carGwv: (data.dienstwagen ?? []).reduce((s, d) => s + (d.gwvBetrag ?? 0), 0),
      fahrradGehaltsumwandlung: data.fahrradGehaltsumwandlung ?? 0,
    })
  }

  const toggleCarExcluded = (index: number) =>
    setExcludedCarIndices(prev => {
      const next = new Set(prev)
      if (next.has(index)) next.delete(index)
      else next.add(index)
      return next
    })

  // The portal quotes the gross (VAT-incl.) rate; the salary conversion uses
  // the net rate (FINN reclaims input VAT). Convert here so both the summary
  // and the eligibility calc work off the net Umwandlungsrate.
  const gehaltsumwandlungBrutto = selected?.gehaltsumwandlung ?? 0
  const gehaltsumwandlung = bruttoToNettoRate(gehaltsumwandlungBrutto)
  const parsedCars = (parsedPayslip?.dienstwagen ?? []).map((c, i, arr) => ({
    label: c.bezeichnung || `Dienstwagen ${arr.length > 1 ? i + 1 : ''}`.trim(),
    eigenanteil: c.gehaltsumwandlung ?? 0,
    gwv: c.gwvBetrag ?? 0,
  }))
  const hasParsedCars = parsedCars.length > 0

  const totalExistingDeductions = computeExistingMonthly({
    bavAN: deductions.bavAN,
    firmenfitness: deductions.firmenfitness,
    fahrradGehaltsumwandlung: deductions.fahrradGehaltsumwandlung,
    cars: parsedCars.map(c => ({ eigenanteil: c.eigenanteil, gwv: c.gwv })),
    excludedCarIndices,
    carGehaltsumwandlungFlat: deductions.carGehaltsumwandlung,
    carGwvFlat: deductions.carGwv,
  })

  const setDeductionField = (key: keyof DeductionItems, value: number) => {
    if (key === 'bavAN') setBavAssumed(false)
    setDeductions(d => ({ ...d, [key]: value }))
  }

  const deductionFields: { key: keyof DeductionItems; label: string; tooltip: string }[] = [
    { key: 'bavAN', label: 'bAV Eigenanteil', tooltip: 'Arbeitnehmer-Anteil Betriebliche Altersvorsorge (Entgeltumwandlung). Wenn die Abrechnung keine bAV zeigt, wird konservativ 430 € angenommen.' },
    { key: 'firmenfitness', label: 'Firmenfitness', tooltip: 'Monatlicher Eigenanteil für Firmenfitness / Urban Sports / Wellpass etc.' },
    ...(hasParsedCars ? [] : [
      { key: 'carGehaltsumwandlung' as const, label: 'PKW Eigenanteil', tooltip: 'Gehaltsumwandlung / Eigenanteil für bestehende(n) Dienstwagen — ohne GwV.' },
      { key: 'carGwv' as const, label: 'PKW GwV', tooltip: 'Geldwerter Vorteil PKW — erhöht die Steuerlast und reduziert so das effektive Nettogehalt. Wird konservativ als Abzug gewertet.' },
    ]),
    { key: 'fahrradGehaltsumwandlung', label: 'Jobrad Rate', tooltip: 'Monatliche Gehaltsumwandlung für Fahrrad-Leasing.' },
  ]

  return (
    <div className="min-h-screen bg-zinc-50 p-4 md:p-8">
      <div className="max-w-2xl mx-auto space-y-6">

        {/* Header */}
        <div className="flex items-center gap-3">
          <Car className="h-8 w-8 text-blue-600 shrink-0" />
          <div>
            <h1 className="text-2xl font-bold text-zinc-900">JobAuto Gehaltscheck</h1>
            <p className="text-sm text-zinc-500">Mindestgehalt-Prüfung für Gehaltsumwandlung</p>
          </div>
        </div>

        <StepIndicator current={step} />

        {/* ── Step 1: Subscription auswählen ── */}
        {step === 1 && (
          <div className="bg-white border border-zinc-200 rounded-xl shadow-sm p-6 space-y-5">
            {/* Mode toggle: look up an open request, or enter the rate manually
                (for active subs / swaps where no eligible request exists). */}
            <div className="inline-flex rounded-lg border border-zinc-200 p-0.5 bg-zinc-50">
              {([
                ['search', 'Nach Mitarbeiter suchen'],
                ['manual', 'Manuell eingeben'],
              ] as const).map(([value, label]) => (
                <button
                  key={value}
                  onClick={() => setMode(value)}
                  className={cn(
                    'px-3 py-1.5 rounded-md text-sm font-medium transition-colors',
                    mode === value ? 'bg-white text-zinc-900 shadow-sm' : 'text-zinc-500 hover:text-zinc-800'
                  )}
                >
                  {label}
                </button>
              ))}
            </div>

            {mode === 'search' ? (
              <>
                <div>
                  <h2 className="text-lg font-semibold text-zinc-900">Welche Subscription soll geprüft werden?</h2>
                  <p className="text-sm text-zinc-500 mt-0.5">
                    Gib die FINN-E-Mail des Mitarbeiters ein und wähle die Subscription aus.
                  </p>
                </div>
                <EmployeeSearch initialEmail={initialEmail} onAutoFill={handleAutoFill} />
              </>
            ) : (
              <>
                <div>
                  <h2 className="text-lg font-semibold text-zinc-900">Zahlen manuell eingeben</h2>
                  <p className="text-sm text-zinc-500 mt-0.5">
                    Ohne offene Anfrage — z.B. bei aktiver Subscription, B2C→JobAuto-Wechsel oder
                    einem Mitarbeiter-Swap. Gib die JobAuto-Rate ein und rechne im nächsten Schritt.
                  </p>
                </div>
                <ManualSubForm onSubmit={handleAutoFill} />
              </>
            )}
          </div>
        )}

        {/* ── Step 2: Gehaltscheck ── */}
        {step === 2 && selected && (
          <div className="space-y-5">
            <button
              onClick={() => setStep(1)}
              className="flex items-center gap-1.5 text-sm text-zinc-500 hover:text-zinc-800 transition-colors"
            >
              <ChevronLeft className="h-4 w-4" /> Andere Subscription wählen
            </button>

            {/* Selected car summary */}
            <div className={cn(
              'rounded-xl border p-5',
              selected.pendingApproval ? 'border-orange-200 bg-orange-50' : 'border-zinc-200 bg-white'
            )}>
              <div className="flex items-start justify-between gap-4">
                <div className="space-y-1.5">
                  {selected.make && (
                    <div className="text-xs font-semibold text-zinc-400 uppercase tracking-widest">{selected.make}</div>
                  )}
                  <div className="text-2xl font-bold text-zinc-900">{selected.model || selected.carLabel}</div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <FuelBadge fuel={selected.fuel} />
                    {selected.pendingApproval && (
                      <span className="text-xs px-2 py-0.5 rounded-full bg-orange-100 text-orange-700 font-medium">
                        Zustimmung ausstehend
                      </span>
                    )}
                    {selected.isSwap && (
                      <span className="text-xs px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 font-medium">
                        Swap
                      </span>
                    )}
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <div className="text-3xl font-bold text-zinc-900">{formatCurrency(gehaltsumwandlung)}</div>
                  <div className="text-xs text-zinc-400 mt-0.5 flex items-center justify-end gap-1">
                    Netto-Gehaltsumwandlung / Monat
                    <InfoTooltip>
                      Netto-Entgeltumwandlung (ohne MwSt.) des neuen JobAutos — dieser Betrag wird vom
                      Jahresbrutto abgezogen. FINN ist vorsteuerabzugsberechtigt, daher zählt die
                      Nettorate. Die monatlich wiederkehrenden Add-ons dieser Subscription sind in der
                      Rate bereits enthalten.
                    </InfoTooltip>
                  </div>
                  {gehaltsumwandlungBrutto > 0 && (
                    <div className="text-xs text-zinc-400 mt-0.5">
                      brutto {formatCurrency(gehaltsumwandlungBrutto)} / Monat
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Threshold check */}
            <div className="bg-white border border-zinc-200 rounded-xl shadow-sm p-6 space-y-5">
              <h2 className="text-base font-semibold text-zinc-900">Mindestgehalt-Prüfung</h2>

              <PayslipUpload onParsed={applyParsedPayslip} onError={() => {}} />

              {/* Per-benefit summary after payslip parse */}
              {parsedPayslip && (
                <BenefitSummary
                  parsedPayslip={parsedPayslip}
                  onEdit={() => setShowEditModal(true)}
                  isSwap={selected.isSwap}
                  previousSubscriptionId={selected.previousSubscriptionId}
                  excludedCarIndices={excludedCarIndices}
                  onToggleCarExcluded={toggleCarExcluded}
                />
              )}

              {/* Brutto + Monate inputs */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <label className={labelStyles}>
                    Bruttogehalt / Monat
                    <InfoTooltip>Vertragliches Bruttogehalt — ohne geldwerte Vorteile (GwV PKW etc.).</InfoTooltip>
                  </label>
                  <div className="relative">
                    <input
                      type="number"
                      className={inputStyles}
                      value={bruttoMonatlich || ''}
                      placeholder="z.B. 4500"
                      onChange={e => setBruttoMonatlich(Number(e.target.value))}
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 text-sm">€</span>
                  </div>
                </div>

                <div className="space-y-1.5">
                  <label className={labelStyles}>
                    Monate mit JobAuto
                    <InfoTooltip>Wie viele Monate läuft die neue Gehaltsumwandlung im aktuellen Kalenderjahr?</InfoTooltip>
                  </label>
                  <div className="flex items-center gap-2">
                    <input
                      type="number"
                      min={1}
                      max={12}
                      className={inputStyles}
                      value={monateImJahr}
                      onChange={e => setMonateImJahr(Math.min(12, Math.max(1, Number(e.target.value))))}
                    />
                    <span className="text-zinc-400 text-sm shrink-0">/ 12</span>
                  </div>
                </div>
              </div>

              {/* Itemized deduction inputs */}
              <div className="space-y-2">
                <div className="flex items-center gap-1">
                  <label className={`${labelStyles} text-zinc-500 uppercase text-xs tracking-wide`}>
                    Bestehende Entgeltumwandlungen / Monat
                  </label>
                  <InfoTooltip>Alle laufenden Gehaltsumwandlungen außer dem neuen JobAuto. Werden mit ×12 aufs Jahr hochgerechnet. Werden automatisch aus der Gehaltsabrechnung befüllt; PKW-Werte pro Fahrzeug oben.</InfoTooltip>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  {deductionFields.map(({ key, label, tooltip }) => (
                    <div key={key} className="space-y-1">
                      <label className={labelStyles}>
                        {label}
                        <InfoTooltip>{tooltip}</InfoTooltip>
                        {key === 'bavAN' && bavAssumed && (
                          <span className="inline-flex items-center rounded-full bg-amber-100 text-amber-700 px-1.5 py-0.5 text-[10px] font-medium">
                            Annahme
                          </span>
                        )}
                      </label>
                      <div className="relative">
                        <input
                          type="number"
                          className={inputStyles}
                          value={deductions[key] || ''}
                          placeholder="0"
                          onChange={e => setDeductionField(key, Number(e.target.value))}
                        />
                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 text-sm">€</span>
                      </div>
                    </div>
                  ))}
                </div>
                {totalExistingDeductions > 0 && (
                  <div className="text-xs text-zinc-400 text-right">
                    Gesamt: {formatCurrency(totalExistingDeductions)}/Monat · {formatCurrency(totalExistingDeductions * 12)}/Jahr
                  </div>
                )}
              </div>

              {/* Eligibility result */}
              <EligibilityResult
                bruttoMonatlich={bruttoMonatlich}
                monateImJahr={monateImJahr}
                neuesJobautoRate={gehaltsumwandlung}
                bavAN={deductions.bavAN}
                bavAssumed={bavAssumed}
                firmenfitness={deductions.firmenfitness}
                fahrradGehaltsumwandlung={deductions.fahrradGehaltsumwandlung}
                cars={parsedCars}
                excludedCarIndices={excludedCarIndices}
                carGehaltsumwandlungFlat={deductions.carGehaltsumwandlung}
                carGwvFlat={deductions.carGwv}
                mindestgrenze={mindestgrenze}
                onMindestgrenzeChange={(v) => setMindestgrenze(Number.isFinite(v) ? Math.max(0, v) : 0)}
              />
            </div>
          </div>
        )}
      </div>

      {/* Re-edit modal for confirmed payslip */}
      {showEditModal && parsedPayslip && (
        <PayslipConfirmModal
          data={parsedPayslip}
          onConfirm={(updated) => {
            applyParsedPayslip(updated)
            setShowEditModal(false)
          }}
          onCancel={() => setShowEditModal(false)}
        />
      )}
    </div>
  )
}
